import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  type CalculateSavedPaymentAccountTransactionsResponse,
  type CurrencyPrimitive,
  EnumCashierTxStatus,
  EnumErrorKey,
  type SavedPaymentAccountProfitAmount,
  type SavedPaymentAccountRefundAmounts,
  type TransactionSummary,
} from "@/api/cashier";
import { saveWithdrawalRequestMutation } from "@/api/cashier/@tanstack/react-query.gen";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { BaseModal } from "@/components/general/base-modal/BaseModal";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { LastUpdatedCounter } from "@/components/last-updated-counter/LastUpdatedCounter";
import { notify } from "@/components/toast/notify.ts";
import toastStyles from "@/components/toast/Toast.module.css";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useLogger } from "@/hooks/useLogger";
import {
  FunnelFlow,
  FunnelStep,
  trackFunnelStep,
} from "@/observability/funnel";
import { BreakdownAccordion } from "@/pages/withdrawals/summary/breakdown-accordion/BreakdownAccordion";
import { SummaryCard } from "@/pages/withdrawals/summary/summary-card/SummaryCard";
import { useSummaryData } from "@/pages/withdrawals/summary/useSummaryData";
import { WithdrawalExceedsModal } from "@/pages/withdrawals/summary/withdrawal-exceeds-modal/WithdrawalExceedsModal";
import type { NavigatableWithdrawalProfitAmounts } from "@/pages/withdrawals/types";
import { EIcon, ERoutes } from "@/utils/enums";
import { moneyToMajorUnits, toMinorUnits } from "@/utils/helpers";
import { serializeError } from "@/utils/log";
import { createPaymentTracker } from "@/utils/payment-tracking";

const EMPTY_CONVERSION_CURRENCIES: Record<string, string> = {};

const TRANSACTIONS_REFETCH_MS = 20_000;

const groupRefundsBySpa = (
  refunds: { id: string; currency: string; amount: number }[],
  currencies: Record<string, CurrencyPrimitive> | undefined,
  conversionCurrencies: Record<string, string> = {}
): {
  refundAmounts: SavedPaymentAccountRefundAmounts[];
  allResolved: boolean;
} => {
  const grouped = new Map<string, { amount: string; code: string }[]>();
  let allResolved = true;

  for (const refund of refunds) {
    // Refund amounts are denominated in each deposit's own currency (refund.currency),
    // which can differ from the trading account currency. Convert to minor units using
    // that currency's decimals - using the trading account's decimals scales the amount
    // by the wrong factor whenever they differ (e.g. a USD refund into a 0-decimal JPY
    // account would be sent 100x too small).
    const currency = currencies?.[refund.currency];
    if (!currency) {
      // Currency metadata is missing (still loading, fetch failed, or unknown code).
      // Flag it so callers block calculation/submission rather than silently dropping
      // this refund SPA from the request body.
      allResolved = false;
      continue;
    }

    const minorAmount = toMinorUnits(refund.amount, currency.decimals);
    if (!minorAmount) continue;

    const existing = grouped.get(refund.id) ?? [];
    existing.push({
      amount: String(minorAmount),
      code: refund.currency,
    });
    grouped.set(refund.id, existing);
  }

  return {
    refundAmounts: Array.from(grouped.entries()).map(([id, amounts]) => ({
      id,
      amounts,
      selected_currency: conversionCurrencies[id] ?? undefined,
    })),
    allResolved,
  };
};

const buildProfitAmount = (
  profitAmounts: Record<string, number | undefined> | undefined,
  decimals: number,
  currencyCode: string,
  selectedCurrency?: string
): SavedPaymentAccountProfitAmount | undefined => {
  if (!profitAmounts) return undefined;

  const entry = Object.entries(profitAmounts).find(
    ([, value]) => value !== undefined && value > 0
  );
  if (!entry) return undefined;

  const [methodId, value] = entry;
  if (value === undefined) return undefined;
  const minorAmount = toMinorUnits(value, decimals);
  if (!minorAmount) return undefined;

  return {
    id: methodId,
    selected_currency: selectedCurrency ?? undefined,
    amount_with_currency: {
      amount: String(minorAmount),
      code: currencyCode,
    },
  };
};

//TODO fix refund amounts and state being passed to summary page
export const Summary = () => {
  const { t } = useTranslation();
  const logger = useLogger();
  const tracker = useMemo(() => createPaymentTracker(logger), [logger]);
  const { selectedAccount } = useAccount();
  const {
    currencies,
    isLoading: currenciesIsLoading,
    refetch: refetchCurrencies,
  } = useCurrencies();
  const location = useLocation();
  const navigate = useNavigate();
  const confirmWithdrawalRef = useRef<HTMLDivElement>(null);

  const withdrawalProfitAmounts:
    | NavigatableWithdrawalProfitAmounts
    | undefined = location.state?.profitAmounts;
  const withdrawalRefundAmounts:
    | { id: string; currency: string; amount: number }[]
    | undefined = location.state?.refundAmounts;
  const profitConversionCurrency: string | undefined =
    location.state?.profitConversionCurrency;
  const refundConversionCurrencies: Record<string, string> =
    location.state?.refundConversionCurrencies ?? EMPTY_CONVERSION_CURRENCIES;
  // Calculation already performed on the choose-method step right before
  // navigating here - seed the query with it instead of re-fetching.
  const calculatedTransactions:
    | CalculateSavedPaymentAccountTransactionsResponse
    | undefined = location.state?.calculatedTransactions;
  const calculatedTransactionsFetchedAt: number | undefined =
    location.state?.calculatedTransactionsFetchedAt;
  const bankTransferSpaIds: string[] = location.state?.bankTransferSpaIds ?? [];

  useEffect(() => {
    if (!confirmWithdrawalRef?.current) {
      return;
    }
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const behavior = prefersReducedMotion ? "auto" : "smooth";
    confirmWithdrawalRef.current.scrollIntoView({
      behavior,
    });
  }, []);

  const { refundAmounts, refundsResolved } = useMemo(() => {
    if (!selectedAccount || !withdrawalRefundAmounts) {
      return { refundAmounts: [], refundsResolved: true };
    }
    const { refundAmounts, allResolved } = groupRefundsBySpa(
      withdrawalRefundAmounts,
      currencies,
      refundConversionCurrencies
    );
    return { refundAmounts, refundsResolved: allResolved };
  }, [
    withdrawalRefundAmounts,
    selectedAccount,
    currencies,
    refundConversionCurrencies,
  ]);

  const profitAmount = useMemo(
    () =>
      selectedAccount
        ? buildProfitAmount(
            withdrawalProfitAmounts?.profitAmounts,
            selectedAccount.currency.decimals,
            selectedAccount.currency.code,
            profitConversionCurrency
          )
        : undefined,
    [
      withdrawalProfitAmounts?.profitAmounts,
      selectedAccount,
      profitConversionCurrency,
    ]
  );

  const {
    data: transactions,
    error: transactionsError,
    isLoading: transactionsIsLoading,
    isRefetching: transactionsIsRefetching,
    isRefetchError: transactionsIsRefetchError,
    refetch: transactionsRefetch,
    dataUpdatedAt: lastUpdated,
  } = useSummaryData({
    selectedAccount,
    refundAmounts,
    profitAmount,
    // Don't calculate with a partial refund list - wait until every refund
    // currency has resolved (see refundsResolved).
    enabled: refundsResolved,
    initialData: calculatedTransactions,
    initialDataUpdatedAt: calculatedTransactionsFetchedAt,
  });

  // While currencies are still loading the refund list is intentionally
  // incomplete, so treat it as loading rather than rendering partial data.
  const refundCurrenciesLoading = !refundsResolved && currenciesIsLoading;
  // Currencies finished loading (or failed) but a refund currency still can't be
  // resolved - fail loudly instead of submitting a partial/empty refund list.
  const refundCurrenciesUnresolved = !refundsResolved && !currenciesIsLoading;
  const summaryIsLoading = transactionsIsLoading || refundCurrenciesLoading;

  const nextUpdate = useMemo(
    () =>
      transactions?.exchange_rate_valid_until?.valueOf() ??
      lastUpdated.valueOf() + TRANSACTIONS_REFETCH_MS, // fallback to 20s
    [transactions?.exchange_rate_valid_until, lastUpdated]
  );

  const { formatAmount } = useFormatAmount();

  const isInsufficientFundsModalOpen = useRef(false);
  const isLimitExceeded = useRef(false);

  const { mutateAsync: processWithdrawalAsync, isPending } = useMutation(
    saveWithdrawalRequestMutation()
  );

  const handleInsufficientFundsClose = useCallback(() => {
    isInsufficientFundsModalOpen.current = false;
    navigate(`../${ERoutes.ChooseMethod}`, { state: location.state });
  }, [navigate, location.state]);

  isLimitExceeded.current = useMemo(() => {
    const apiError = transactionsError as { key?: string; message?: string };
    return (
      apiError?.key === EnumErrorKey.WithdrawalInsufficientFunds &&
      !isInsufficientFundsModalOpen.current
    );
  }, [transactionsError]);

  const handleLimitExceededClose = useCallback(() => {
    isLimitExceeded.current = false;
    navigate(`../${ERoutes.Accounts}`, { state: location.state });
  }, [navigate, location.state]);

  const handleNext = useCallback(async () => {
    // Never submit a partial refund list - block until every refund currency resolves.
    if (!transactions || !selectedAccount || !refundsResolved) return;

    trackFunnelStep(FunnelFlow.Withdrawal, FunnelStep.ExecuteClicked);

    const totalAmount = moneyToMajorUnits(transactions.total_money) ?? 0;
    const methodId =
      profitAmount?.id ?? withdrawalRefundAmounts?.[0]?.id ?? "unknown";

    try {
      const result = await processWithdrawalAsync({
        body: {
          refund_saved_payment_accounts: refundAmounts,
          profit_saved_payment_account: profitAmount,
        },
        path: {
          tradingAccountID: selectedAccount.id,
        },
      });

      navigate(
        `../${ERoutes.Verification}/${result.withdrawal_request_id}?verification=${result.verification_type}`,
        {
          state: {
            withdrawal_request_id: result.withdrawal_request_id,
            verification_type: result.verification_type,
            created_at: result.created_at,
            time_limit_in_minutes: result.time_limit_in_minutes,
            selectedAccount,
            transactionData: transactions,
          },
        }
      );
    } catch (err) {
      const apiError = err as { key?: string; message?: string };
      if (apiError?.key === EnumErrorKey.WithdrawalInsufficientFunds) {
        isInsufficientFundsModalOpen.current = true;
        return;
      }
      if (apiError?.key === EnumErrorKey.WithdrawalBankStatementRequired) {
        notify.error(
          <div className={toastStyles.ToastBody}>
            <span className={toastStyles.ToastTitle}>
              {t("withdrawals.bank_statement.mandatory_toast_title")}
            </span>
            <span className={toastStyles.ToastDescription}>
              {t("withdrawals.bank_statement.mandatory_toast")}
            </span>
          </div>
        );
        return;
      }
      tracker.withdrawal.failed({
        methodId,
        amount: totalAmount,
        error: apiError?.message ?? "Unknown error",
        errorCode: "WITHDRAWAL_FAILED",
      });
      navigate(`../${ERoutes.ErrorPage}`, {
        state: {
          balance: selectedAccount.balance,
          money: transactions.total_money,
          status: EnumCashierTxStatus.CashierTxUnsuccessful,
          external_trading_account_id: "",
        } as TransactionSummary,
      });
    }
  }, [
    transactions,
    processWithdrawalAsync,
    selectedAccount,
    tracker,
    profitAmount,
    refundAmounts,
    refundsResolved,
    withdrawalRefundAmounts,
    navigate,
    t,
  ]);

  if (!withdrawalProfitAmounts || !selectedAccount) {
    navigate(`../${ERoutes.ChooseMethod}`);
    return null;
  }

  if (transactionsError) {
    logger.error("Failed to calculate withdrawal transactions", {
      error: serializeError(transactionsError),
      transactionsIsRefetchError,
    });
    if (!transactionsIsRefetchError) {
      return <ErrorPage buttonAction={transactionsRefetch} />;
    }
  }

  if (refundCurrenciesUnresolved) {
    // Some refund currencies could not be resolved (unknown code or failed
    // currencies fetch). Fail loudly instead of silently dropping refund SPAs
    // from the calculation/submission.
    logger.error("Unresolved refund currencies", {
      withdrawalRefundAmounts,
    });
    return <ErrorPage buttonAction={refetchCurrencies} />;
  }

  return (
    <div ref={confirmWithdrawalRef} className="ps-flex ps-flex-col ps-gap-8">
      <div className="ps-flex ps-flex-row-wrap ps-gap-8 ps-items-center ps-justify-between">
        <h1 className="ps-flex-1">
          {t("withdrawals.summary.confirm_withdrawal")}
        </h1>
        <LastUpdatedCounter
          isLoading={summaryIsLoading}
          lastUpdated={lastUpdated}
          nextUpdate={nextUpdate}
          onUpdate={transactionsRefetch}
        />
      </div>

      <SummaryCard
        isLoading={summaryIsLoading}
        selectedAccount={selectedAccount}
        transactions={transactions}
      >
        <BreakdownAccordion
          transactions={transactions}
          isLoading={summaryIsLoading}
          isUpdating={transactionsIsRefetching}
          onUpdate={transactionsRefetch}
          lastUpdated={lastUpdated}
          nextUpdate={nextUpdate}
          bankTransferSpaIds={bankTransferSpaIds}
        />
      </SummaryCard>
      <div className="ps-flex ps-justify-end">
        <NavButtons
          className="ps-max-w-[480px]"
          backAction={() =>
            navigate(`../${ERoutes.ChooseMethod}`, {
              state: location.state,
            })
          }
          nextLabel={t("global.nav_buttons.confirm")}
          nextAction={handleNext}
          backDisabled={isPending}
          nextDisabled={isPending || summaryIsLoading}
        />
      </div>
      <BaseModal
        testId="withdrawal-error-modal-unable-to-load-information"
        isOpen={
          !isInsufficientFundsModalOpen.current &&
          !isLimitExceeded.current &&
          transactionsIsRefetchError
        }
        onClose={transactionsRefetch}
      >
        <ErrorPage buttonAction={transactionsRefetch} />
      </BaseModal>
      <WithdrawalExceedsModal
        isOpen={
          isLimitExceeded.current && !isInsufficientFundsModalOpen.current
        }
        onClose={handleLimitExceededClose}
        isCancelable
      />
      <BaseModal
        testId="withdrawal-insufficient-funds-modal"
        isOpen={
          !isLimitExceeded.current && isInsufficientFundsModalOpen.current
        }
        onClose={handleInsufficientFundsClose}
        isCancelable
      >
        <ErrorPage
          icon={EIcon.ErrorRedCrossIcon}
          title={t("withdrawals.summary.insufficient_funds_modal.title")}
          message={t(
            "withdrawals.summary.insufficient_funds_modal.description",
            {
              amount: formatAmount(
                moneyToMajorUnits(transactions?.available_money),
                selectedAccount?.currency
              ),
            }
          )}
          buttonLabel={t("withdrawals.summary.insufficient_funds_modal.button")}
          buttonAction={handleInsufficientFundsClose}
        />
      </BaseModal>
    </div>
  );
};
